// angular dependencies
import { inject, type Signal } from '@angular/core';

// third-party dependencies
import {
  injectQuery,
  keepPreviousData,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

// fantaz dependencies
import { DataAccessTournamentsService } from '@fantaz/shared/data-access/api';
import { SchedulesRecordItem } from '@fantaz/shared/data-access/models';

/**
 * Creates a query for fetching the next events based on the provided prototype and schedule IDs.
 *
 * @param prototypeId - A signal containing the prototype ID, which can be a string or undefined.
 * @param scheduleId - A signal containing the schedule ID, which can be a string or undefined.
 * @returns The result of the query, including the query key, query function, placeholder data, stale time, and refetch interval.
 */
export function nextEventsQuery(
  params: {
    prototypeId: Signal<string | undefined>;
    scheduleId: Signal<string | undefined>;
  },
  options: {
    enabled: Signal<boolean>;
  },
) {
  const service = inject(DataAccessTournamentsService);

  return injectQuery(() => ({
    queryKey: ['next-events', params.prototypeId(), params.scheduleId()],
    queryFn: () =>
      lastValueFrom(
        service.fetchNextEvents({
          prototypeId: params.prototypeId()!,
          scheduleId: params.scheduleId()!,
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
    refetchInterval: 1000 * 60 * 5, // 5 minutes to refetch
    enabled: options.enabled(),
  }));
}

export function multiNextEventsQuery(
  schedules: Signal<SchedulesRecordItem[] | undefined>,
  options: {
    key: Signal<string>;
    staleTime?: number;
    refetchInterval?: number;
  },
) {
  options = {
    staleTime: 30000,
    refetchInterval: 30000,
    ...options,
  };
  const service = inject(DataAccessTournamentsService);

  return injectQuery(() => ({
    queryKey: ['tournament-next-events', options.key()],
    queryFn: () =>
      lastValueFrom(
        service.fetchTournamentRoomsNextEvents({
          schedules: schedules()!,
        }),
      ),
    placeholderData: keepPreviousData,
    enabled: (schedules() ?? []).length > 0,
    retry: 3,
    ...options,
  }));
}
